import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Container, SectionTitle, SectionSubtitle } from '../../styles/Layout';
import { 
  FaUsers, 
  FaChartLine, 
  FaMobile, 
  FaShieldAlt, 
  FaClock,
  FaMapMarkerAlt,
  FaBell,
  FaCalendarAlt,
  FaMoneyBillWave,
  FaUserCheck,
  FaBook,
  FaArrowRight,
  FaCheck,
  FaCog,
  FaRocket,
  FaDatabase,
  FaCloud,
  FaLaptopCode,
  FaTools,
  FaAward,
  FaLightbulb,
  FaHandshake,
  FaPlay,
  FaBrain,
  FaSearch,
  FaSync,
  FaChartBar,
  FaGlobe,
  FaHeadset
} from 'react-icons/fa';

const ProductPageContainer = styled.section`
  padding-top: 100px;
  min-height: 100vh;
  background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%);
`;

const HeroSection = styled.section`
  background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 50%, var(--secondary) 100%);
  color: white;
  padding: var(--spacing-xxxl) 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 25% 25%, rgba(0, 212, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 75% 75%, rgba(255, 0, 128, 0.1) 0%, transparent 50%);
    z-index: 1;
  }
`;

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xxxl);
  align-items: center;
  position: relative;
  z-index: 2;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-xl);
    text-align: center;
  }
`;

const HeroContent = styled(motion.div)`
  max-width: 600px;
  
  @media (max-width: 968px) {
    max-width: 100%;
    order: 2;
  }
`;

const HeroVisual = styled(motion.div)`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 968px) {
    order: 1;
    margin-bottom: var(--spacing-xl);
  }
`;

const ProductBadge = styled(motion.div)`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, rgba(0, 212, 255, 0.1) 0%, rgba(255, 0, 128, 0.1) 100%);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 50px;
  padding: 0.8rem 1.5rem;
  margin-bottom: var(--spacing-lg);
  font-size: 0.9rem;
  color: var(--neon-blue);
  font-weight: 600;
  
  .badge-icon {
    margin-right: 8px;
    color: var(--neon-green);
  }
`;

const ProductName = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  margin-bottom: var(--spacing-md);
  font-weight: 900;
  line-height: 1.1;
  
  .primary-text {
    color: var(--text-primary);
  }
  
  .highlight-text {
    background: linear-gradient(135deg, var(--neon-blue) 0%, var(--neon-magenta) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const ProductTagline = styled(motion.h2)`
  font-size: clamp(1.2rem, 3vw, 1.8rem);
  margin-bottom: var(--spacing-lg);
  color: var(--neon-green);
  font-weight: 600;
`;

const ProductSubheading = styled(motion.p)`
  font-size: clamp(1rem, 2vw, 1.3rem);
  margin-bottom: var(--spacing-xl);
  line-height: 1.7;
  color: var(--text-secondary);
`;

const HeroButtons = styled(motion.div)`
  display: flex;
  gap: var(--spacing-lg);
  align-items: center;
  flex-wrap: wrap;
  
  @media (max-width: 968px) {
    justify-content: center;
  }
`;

const PrimaryButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  background: linear-gradient(135deg, var(--neon-blue) 0%, var(--neon-magenta) 100%);
  color: white;
  padding: 1.3rem 2.5rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.1rem;
  text-decoration: none;
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 212, 255, 0.3);
  transition: all 0.3s cubic-bezier(0.33, 1, 0.68, 1);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent);
    transition: left 0.6s;
  }
  
  &:hover {
    box-shadow: 0 15px 50px rgba(0, 212, 255, 0.5);
    transform: translateY(-3px);
    
    &::before {
      left: 100%;
    }
    
    svg {
      transform: translateX(5px);
    }
  }
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
`;

const SecondaryButton = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  color: var(--text-primary);
  font-weight: 600;
  font-size: 1.1rem;
  text-decoration: none;
  padding: 1.3rem 0;
  transition: all 0.3s ease;
  
  &:hover {
    color: var(--neon-blue);
    text-shadow: 0 0 10px currentColor;
    
    .play-button {
      background: var(--neon-blue);
      box-shadow: 0 0 25px var(--neon-blue);
      transform: scale(1.1);
    }
  }
  
  .play-button {
    width: 55px;
    height: 55px;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    backdrop-filter: blur(12px);
    border: 2px solid rgba(255, 255, 255, 0.2);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: var(--spacing-md);
    transition: all 0.3s ease;
    
    svg {
      margin-left: 3px;
      font-size: 1.2rem;
    }
  }
`;

const HeroImageContainer = styled.div`
  position: relative;
  width: 500px;
  height: 400px;
  
  @media (max-width: 968px) {
    width: 350px;
    height: 280px;
  }
`;

const HeroImage = styled(motion.div)`
  width: 100%;
  height: 100%;
  background: ${props => props.bgImage} center/cover;
  border-radius: 24px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.4);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, ${props => props.overlay || 'rgba(0, 212, 255, 0.2)'} 0%, rgba(255, 0, 128, 0.2) 100%);
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    backdrop-filter: blur(2px);
    border: 2px solid rgba(255, 255, 255, 0.2);
    border-radius: 24px;
    z-index: 2;
  }
`;

const OverviewSection = styled.section`
  padding: var(--spacing-xxxl) 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  backdrop-filter: blur(20px);
`;

const OverviewGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--spacing-xxxl);
  align-items: center;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-xl);
  }
`;

const OverviewContent = styled(motion.div)`
  color: var(--text-primary);
  
  h2 {
    font-size: clamp(2rem, 4vw, 3rem);
    margin-bottom: var(--spacing-lg);
    background: linear-gradient(135deg, var(--text-primary) 0%, var(--neon-blue) 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  p {
    font-size: 1.2rem;
    line-height: 1.8;
    color: var(--text-secondary);
    margin-bottom: var(--spacing-lg);
  }
`;

const OverviewImage = styled(motion.div)`
  width: 100%;
  height: 400px;
  background: ${props => props.bgImage} center/cover;
  border-radius: 20px;
  position: relative;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, ${props => props.overlay || 'rgba(0, 255, 136, 0.2)'} 0%, rgba(0, 212, 255, 0.2) 100%);
    z-index: 1;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
    backdrop-filter: blur(2px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 20px;
    z-index: 2;
  }
`;

const FeaturesSection = styled.section`
  padding: var(--spacing-xxxl) 0;
  background: linear-gradient(135deg, var(--primary-dark) 0%, var(--primary) 100%);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 30% 40%, rgba(0, 255, 136, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 70% 60%, rgba(139, 92, 246, 0.1) 0%, transparent 50%);
    z-index: 1;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: var(--spacing-xl);
  margin-top: var(--spacing-xl);
  position: relative;
  z-index: 2;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: var(--spacing-lg);
  }
`;

const FeatureCard = styled(motion.div)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  padding: var(--spacing-xl);
  transition: all 0.3s cubic-bezier(0.33, 1, 0.68, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 2px;
    background: linear-gradient(135deg, ${props => props.color || 'var(--neon-blue)'} 0%, transparent 100%);
  }
  
  &:hover {
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0.08) 100%);
    transform: translateY(-10px);
    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
    border-color: ${props => props.color || 'var(--neon-blue)'};
  }
`;

const FeatureIcon = styled.div`
  width: 70px;
  height: 70px;
  border-radius: 20px;
  background: linear-gradient(135deg, ${props => props.background || 'var(--neon-blue)'} 0%, ${props => props.background || 'var(--neon-blue)'}80 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--spacing-lg);
  font-size: 1.8rem;
  color: white;
  flex-shrink: 0;
  box-shadow: 0 10px 30px ${props => props.background || 'var(--neon-blue)'}40;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%);
    border-radius: 20px;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.4rem;
  margin-bottom: var(--spacing-lg);
  color: var(--text-primary);
  font-weight: 700;
`;

const FeatureDescription = styled.p`
  color: var(--text-secondary);
  line-height: 1.7;
  margin-bottom: var(--spacing-lg);
  font-size: 1.1rem;
`;

const FeatureList = styled.ul`
  list-style-type: none;
  
  li {
    margin-bottom: var(--spacing-md);
    position: relative;
    padding-left: 30px;
    color: var(--text-secondary);
    line-height: 1.6;
    
    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 8px;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: linear-gradient(135deg, var(--neon-green) 0%, var(--neon-blue) 100%);
      box-shadow: 0 0 10px var(--neon-green);
    }
  }
`;

const BenefitsSection = styled.section`
  padding: var(--spacing-xxxl) 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  backdrop-filter: blur(20px);
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: var(--spacing-xl);
  margin-top: var(--spacing-xl);
`;

const BenefitCard = styled(motion.div)`
  text-align: center;
  padding: var(--spacing-xl);
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.4);
    border-color: var(--neon-blue);
  }
`;

const BenefitIcon = styled.div`
  font-size: 3.5rem;
  color: var(--neon-blue);
  margin-bottom: var(--spacing-lg);
`;

const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: var(--spacing-md);
  color: var(--text-primary);
  font-weight: 700;
`;

const BenefitDescription = styled.p`
  color: var(--text-secondary);
  line-height: 1.7;
  font-size: 1.1rem;
`;

const CTASection = styled.section`
  padding: var(--spacing-xxxl) 0;
  background: linear-gradient(135deg, var(--neon-blue) 0%, var(--neon-magenta) 100%);
  color: white;
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: 
      radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
      radial-gradient(circle at 70% 70%, rgba(255, 255, 255, 0.1) 0%, transparent 50%);
    z-index: 1;
  }
`;

const CTAContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 800px;
  margin: 0 auto;
`;

const CTATitle = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  margin-bottom: var(--spacing-lg);
  font-weight: 900;
`;

const CTADescription = styled.p`
  font-size: 1.3rem;
  margin-bottom: var(--spacing-xl);
  opacity: 0.9;
  line-height: 1.7;
`;

const CTAButton = styled.a`
  display: inline-flex;
  align-items: center;
  background: white;
  color: var(--primary-dark);
  padding: 1.5rem 3rem;
  border-radius: 12px;
  font-weight: 700;
  font-size: 1.2rem;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  
  svg {
    margin-left: 10px;
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
    
    svg {
      transform: translateX(5px);
    }
  }
`;

// GeoCRM Product Page
export const GeoCRMPage = () => {
  const coreFeatures = [
    {
      icon: <FaUsers />,
      title: "360° Customer Intelligence",
      description: "Complete customer lifecycle management with AI-powered insights, behavioral analytics, and predictive customer scoring for enhanced relationship building.",
      background: "var(--neon-blue)",
      color: "rgba(0, 212, 255, 0.3)",
      features: [
        "Comprehensive customer profiles with interaction history and preferences",
        "AI-powered customer behavior analysis and predictive insights",
        "Advanced customer segmentation with dynamic targeting capabilities",
        "Real-time customer journey mapping and touchpoint optimization",
        "Automated customer health scoring with risk assessment alerts",
        "Social media integration for complete customer context and engagement"
      ]
    },
    {
      icon: <FaBrain />,
      title: "Intelligent Lead Management",
      description: "AI-driven lead qualification and nurturing system that automatically scores, routes, and manages leads for maximum conversion efficiency.",
      background: "var(--neon-green)",
      color: "rgba(0, 255, 136, 0.3)",
      features: [
        "Machine learning-based lead scoring with predictive conversion rates",
        "Automated lead routing based on territory, expertise, and availability",
        "Intelligent lead nurturing campaigns with personalized content delivery",
        "Real-time lead tracking with comprehensive activity monitoring",
        "Advanced lead qualification workflows with customizable criteria",
        "Integration with marketing automation for seamless lead handoff"
      ]
    },
    {
      icon: <FaChartLine />,
      title: "Advanced Sales Analytics",
      description: "Comprehensive sales performance tracking with predictive analytics, forecasting capabilities, and actionable insights for data-driven decisions.",
      background: "var(--neon-magenta)",
      color: "rgba(255, 0, 128, 0.3)",
      features: [
        "Real-time sales dashboard with customizable KPI tracking and alerts",
        "Predictive sales forecasting with AI-powered trend analysis",
        "Territory and team performance analytics with comparative insights",
        "Pipeline analysis with conversion rate optimization recommendations",
        "Revenue attribution tracking across multiple channels and campaigns",
        "Advanced reporting with automated insights and performance recommendations"
      ]
    },
    {
      icon: <FaSync />,
      title: "Seamless Integration Hub",
      description: "Robust API ecosystem that connects with existing business tools, marketing platforms, and third-party applications for unified operations.",
      background: "var(--neon-purple)",
      color: "rgba(139, 92, 246, 0.3)",
      features: [
        "Pre-built integrations with popular business tools and platforms",
        "RESTful API with comprehensive documentation and developer support",
        "Real-time data synchronization across all connected applications",
        "Custom webhook support for automated workflow triggers",
        "Single sign-on (SSO) integration with enterprise identity providers",
        "Data import/export tools with mapping and validation capabilities"
      ]
    },
    {
      icon: <FaMobile />,
      title: "Mobile-First Experience",
      description: "Native mobile applications with full CRM functionality, offline capabilities, and real-time synchronization for sales teams on the go.",
      background: "var(--neon-yellow)",
      color: "rgba(255, 255, 0, 0.3)",
      features: [
        "Native iOS and Android apps with full feature parity",
        "Offline functionality with automatic sync when connection restored",
        "GPS-based check-ins and location tracking for field sales teams",
        "Mobile-optimized forms and data entry with voice-to-text support",
        "Push notifications for important updates and task reminders",
        "Mobile dashboard with key metrics and performance indicators"
      ]
    },
    {
      icon: <FaShieldAlt />,
      title: "Enterprise Security & Compliance",
      description: "Bank-grade security with comprehensive compliance features, audit trails, and data protection measures for enterprise-level requirements.",
      background: "var(--neon-blue)",
      color: "rgba(0, 212, 255, 0.3)",
      features: [
        "End-to-end encryption for data at rest and in transit",
        "Role-based access control with granular permission management",
        "Comprehensive audit trails with detailed activity logging",
        "GDPR, CCPA, and SOC 2 compliance with automated reporting",
        "Multi-factor authentication with biometric support",
        "Regular security assessments and penetration testing"
      ]
    }
  ];

  const benefits = [
    {
      icon: <FaRocket />,
      title: "Accelerated Sales Performance",
      description: "Boost sales productivity by 40% with intelligent automation, predictive insights, and streamlined workflows that eliminate manual tasks and focus on high-value activities."
    },
    {
      icon: <FaBrain />,
      title: "AI-Powered Decision Making",
      description: "Leverage machine learning algorithms to predict customer behavior, identify upselling opportunities, and optimize sales strategies with data-driven recommendations."
    },
    {
      icon: <FaGlobe />,
      title: "Unified Customer Experience",
      description: "Create seamless customer journeys across all touchpoints with integrated communication channels, personalized interactions, and consistent service delivery."
    },
    {
      icon: <FaChartBar />,
      title: "Advanced Analytics & Insights",
      description: "Gain deep visibility into sales performance, customer trends, and market opportunities with comprehensive reporting and predictive analytics capabilities."
    },
    {
      icon: <FaCloud />,
      title: "Scalable Cloud Architecture",
      description: "Built on modern cloud infrastructure that scales automatically with your business growth, ensuring consistent performance and reliability at any size."
    },
    {
      icon: <FaHeadset />,
      title: "24/7 Expert Support",
      description: "Dedicated customer success team with comprehensive onboarding, training, and ongoing support to ensure maximum ROI and user adoption across your organization."
    }
  ];

  return (
    <ProductPageContainer>
      <HeroSection>
        <Container>
          <HeroGrid>
            <HeroContent
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, ease: "easeOut" }}
            >
              <ProductBadge
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <FaAward className="badge-icon" />
                Next-Generation CRM Platform
              </ProductBadge>
              
              <ProductName
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <span className="primary-text">GeoCRM</span>
                <span className="highlight-text">Smart Customer Hub</span>
              </ProductName>
              
              <ProductTagline
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                AI-Powered Customer Relationship Management
              </ProductTagline>
              
              <ProductSubheading
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              >
                Transform your customer relationships with intelligent automation, predictive analytics, and seamless integration. GeoCRM empowers sales teams to build stronger relationships, close more deals, and drive sustainable business growth.
              </ProductSubheading>
              
              <HeroButtons
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <PrimaryButton 
                  href="#contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Start Free Trial <FaArrowRight />
                </PrimaryButton>
                
                <SecondaryButton 
                  href="#features"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <div className="play-button">
                    <FaPlay />
                  </div>
                  Watch Demo
                </SecondaryButton>
              </HeroButtons>
            </HeroContent>

            <HeroVisual
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <HeroImageContainer>
                <HeroImage
                  bgImage="url('https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80')"
                  overlay="rgba(0, 212, 255, 0.3)"
                  animate={{
                    y: [0, -10, 0],
                  }}
                  transition={{
                    duration: 4,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
              </HeroImageContainer>
            </HeroVisual>
          </HeroGrid>
        </Container>
      </HeroSection>

      <OverviewSection>
        <Container>
          <OverviewGrid>
            <OverviewContent
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2>Revolutionizing Customer Relationships</h2>
              <p>
                GeoCRM is a comprehensive, AI-powered customer relationship management platform designed for modern businesses seeking to maximize customer lifetime value and streamline sales operations. Our intelligent system combines traditional CRM functionality with cutting-edge artificial intelligence and machine learning capabilities.
              </p>
              <p>
                Built on a cloud-native architecture, GeoCRM offers seamless scalability and integration capabilities that grow with your business. The platform features intelligent automation tools that eliminate manual data entry, advanced analytics dashboards that provide actionable insights, and customizable workflows that adapt to your unique business processes.
              </p>
              <p>
                From lead generation and opportunity management to customer service and retention analytics, GeoCRM provides a unified platform that transforms how businesses interact with their customers throughout the entire customer lifecycle, driving sustainable growth and competitive advantage.
              </p>
            </OverviewContent>
            
            <OverviewImage
              bgImage="url('https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80')"
              overlay="rgba(0, 255, 136, 0.2)"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              animate={{
                y: [0, -15, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
            />
          </OverviewGrid>
        </Container>
      </OverviewSection>

      <FeaturesSection id="features">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <SectionTitle style={{ color: 'white', textAlign: 'center' }}>
              Comprehensive CRM Features
            </SectionTitle>
            <SectionSubtitle $dark style={{ textAlign: 'center' }}>
              Powerful modules designed to optimize every aspect of customer relationship management with intelligent automation and seamless integration capabilities.
            </SectionSubtitle>
          </motion.div>
          
          <FeaturesGrid>
            {coreFeatures.map((feature, index) => (
              <FeatureCard
                key={index}
                color={feature.color}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <FeatureIcon background={feature.background}>
                  {feature.icon}
                </FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
                <FeatureList>
                  {feature.features.map((item, itemIndex) => (
                    <li key={itemIndex}>{item}</li>
                  ))}
                </FeatureList>
              </FeatureCard>
            ))}
          </FeaturesGrid>
        </Container>
      </FeaturesSection>

      <BenefitsSection>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <SectionTitle style={{ color: 'white', textAlign: 'center' }}>
              Why Choose GeoCRM?
            </SectionTitle>
            <SectionSubtitle $dark style={{ textAlign: 'center' }}>
              Transform your sales operations with intelligent automation, predictive insights, and seamless customer experience management that drives measurable business growth.
            </SectionSubtitle>
          </motion.div>
          
          <BenefitsGrid>
            {benefits.map((benefit, index) => (
              <BenefitCard
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
              >
                <BenefitIcon>{benefit.icon}</BenefitIcon>
                <BenefitTitle>{benefit.title}</BenefitTitle>
                <BenefitDescription>{benefit.description}</BenefitDescription>
              </BenefitCard>
            ))}
          </BenefitsGrid>
        </Container>
      </BenefitsSection>

      <CTASection>
        <Container>
          <CTAContent>
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <CTATitle>Ready to Transform Your Sales Performance?</CTATitle>
              <CTADescription>
                Join thousands of businesses already using GeoCRM to accelerate their sales growth. Start your free trial today and experience the power of intelligent customer relationship management.
              </CTADescription>
              <CTAButton href="#contact">
                Start Your Free Trial <FaArrowRight />
              </CTAButton>
            </motion.div>
          </CTAContent>
        </Container>
      </CTASection>
    </ProductPageContainer>
  );
};